import { connect } from 'react-redux';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { IconSpinner, IconThumbsDown, IconThumbsUp } from '../../../ui-kit/icons';
import moment from 'moment';

import { ButtonGroup, Button, Dropdown } from '../../../ui-kit';
import { dutyTransactionHtmlLink, dutyTransactionPath, isRegardingUser } from '../../../../utils/dutyTransactions';
import { operations as dutyTransactionOperations } from '../../../../state/modules/duty-transactions';
import { formatTime } from '../../../../utils/time';
import { getCurrentUser } from '../../../../state/modules/current-user/operations';
import { groupSchedulePath } from '../../../../utils/paths';
import { selectCurrentUserId } from '../../../../state/modules/current-user/selectors';
import { userColumn } from '../../../activity-report-form/shared/shared-columns';
import AvailabilityCodeIcon from '../../../general/AvailabilityCodeIcon';
import DashboardAPIManager from '../../DashboardAPIManager';
import DutyTransactionTypeIndicator, {
  dutyTransactionTypeId,
  allTypeIds
} from '../../../schedule/exchanges/DutyTransactionTypeIndicator';
import ReactTable, { multiSelectFilterColumn, timeColumn } from '../../../../utils/react-table';
import ScheduleWarningsPopoverConnected from '../../../schedule/warnings/ScheduleWarningsPopoverConnected';
import SettingsForm from '../../widgets/ui-kit/SettingsForm';
import TransactionStateLabel, { allStates } from '../../../schedule/exchanges/TransactionStateLabel';
import TransactionUserStateLabel, { allUserStates } from '../../../schedule/exchanges/TransactionUserStateLabel';
import dutyTransactionSelectors from '../../../../state/modules/duty-transactions/selectors';

// This widget is used to show all duty transactions (DutyRequests,
// AbsenceRequests and DutyExchanges) for 1 user

const FIFTEEN_MINUTES = 15 * 60 * 1000;

// accepting transaction is the transaction which is currently being accepted
const ActionButtons = ({ transaction, acceptTransaction, rejectTransaction, acceptingTransaction }) => {
  const canRespond = transaction.userState === 'pending' && transaction.state !== 'completed';
  const { group_id, start_time, end_time } = transaction;
  const loading = acceptingTransaction && acceptingTransaction.id === transaction.id;

  const respondButtons = canRespond && (
    <ButtonGroup>
      <Button onClick={() => acceptTransaction(transaction)} variant="success" size="sm" disabled={loading}>
        {loading ? <IconSpinner className="fa-spin" /> : <IconThumbsUp />}
      </Button>
      <Button onClick={() => rejectTransaction(transaction)} variant="danger" size="sm" disabled={loading}>
        {loading ? <IconSpinner className="fa-spin" /> : <IconThumbsDown />}
      </Button>
    </ButtonGroup>
  );

  return (
    <>
      <Dropdown as={ButtonGroup}>
        <Button size="sm" variant="secondary" href={dutyTransactionPath(transaction)}>
          {I18n.t('show')}
        </Button>
        <Dropdown.Toggle size="sm" split variant="secondary" />
        <Dropdown.Menu>
          <Dropdown.Item eventKey="1" href={groupSchedulePath(group_id, start_time, end_time)} target="_blank">
            {I18n.t('show_schedule')}
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>{' '}
      {respondButtons}
    </>
  );
};

// For react table we need to define:
// See https://github.com/tannerlinsley/react-table/tree/v6#columns
const instantiateColumns = ({
  userId,
  acceptTransaction,
  rejectTransaction,
  showDatePicker = true,
  showRequestTypeFilter = true,
  acceptingTransaction
}) => [
  multiSelectFilterColumn({
    maxWidth: 140,
    show: showRequestTypeFilter,
    // machine readable designator for the type of request
    accessor: d => dutyTransactionTypeId(d, userId),
    Header: I18n.t('attributes.type'),
    filterable: true,
    id: 'type',
    Cell: row => <DutyTransactionTypeIndicator transaction={row.original} userId={userId} />,
    // render the select options for the multi select filter in the column header
    selectOptions: allTypeIds.sort().map(type => ({
      value: type,
      html: <DutyTransactionTypeIndicator typeId={type} />
    }))
  }),
  userColumn(),
  timeColumn({
    showDatePicker,
    dateOnly: true,
    minWidth: 120,
    Header: I18n.t('attributes.start_time'),
    id: 'start_time',
    accessor: d => d.start_time,
    Cell: row => dutyTransactionHtmlLink(row.original, formatTime(row.original.start_time))
  }),
  timeColumn({
    showDatePicker,
    dateOnly: true,
    minWidth: 120,
    Header: I18n.t('attributes.end_time'),
    id: 'end_time',
    accessor: d => d.end_time,
    Cell: row => dutyTransactionHtmlLink(row.original, formatTime(row.original.end_time))
  }),
  {
    Header: I18n.t('activerecord.models.schedule/availability_code.one'),
    filterable: true,
    accessor: d => d.availabilityCode && d.availabilityCode.name,
    id: 'availability_code',
    Cell: row => {
      const data = row.original;
      if (!data.availabilityCode) return null;
      return <AvailabilityCodeIcon code={data.availabilityCode} />;
    }
  },
  multiSelectFilterColumn({
    maxWidth: 100,
    Header: I18n.t('attributes.state'),
    accessor: d => d.state,
    id: 'state',
    Cell: row => <TransactionStateLabel transactionState={row.original.state} />,
    // render the select options for the multi select filter in the column header
    selectOptions: allStates.sort().map(state => ({
      value: state,
      html: <TransactionStateLabel transactionState={state} />
    }))
  }),
  multiSelectFilterColumn({
    maxWidth: 100,
    Header: I18n.t('activerecord.attributes.schedule/exchanges/duty_exchange_interval.user_state'),
    accessor: d => d.userState,
    id: 'userState',
    Cell: row => <TransactionUserStateLabel transactionState={row.original.userState} />,
    // render the select options for the multi select filter in the column header
    selectOptions: allUserStates.sort().map(state => ({
      value: state,
      html: <TransactionUserStateLabel transactionState={state} />
    }))
  }),
  {
    Header: I18n.t('groups.types.group'),
    accessor: d => d.station && d.station.name,
    id: 'groups',
    filterable: true,
    Cell: row => {
      const data = row.original;
      const { station, station_id } = data;
      const url = `/groups/${station_id}`;
      return (
        <a href={url} target="_blank" rel="noreferrer">
          {station && station.name}
        </a>
      );
    }
  },
  {
    Header: I18n.t('comments'),
    accessor: d => d.comments,
    id: 'comments',
    filterable: true,
    Cell: row => {
      const data = row.original;
      return <div>{data.comments}</div>;
    }
  },
  {
    // action buttons:
    // - open request
    // - show group rota
    Header: '',
    filterable: false,
    sortable: false,
    minWidth: 90,
    className: 'td-action',
    Cell: row => {
      const data = row.original;

      return (
        <ActionButtons
          transaction={data}
          acceptTransaction={acceptTransaction}
          rejectTransaction={rejectTransaction}
          acceptingTransaction={acceptingTransaction}
        />
      );
    }
  }
];

const viewTypeOptions = () =>
  ['my_requests', 'all_requests'].map(p => ({
    value: p,
    name: I18n.t(`dashboards.widget_archetypes.user_duty_transactions.view_types.${p}`)
  }));

const Settings = ({ updateWidgetSettingsField, widgetSettings }) => (
  <SettingsForm updateWidgetSettingsField={updateWidgetSettingsField} widgetSettings={widgetSettings}>
    <SettingsForm.Section>
      <SettingsForm.Section.Header>
        {I18n.t('dashboards.widget_archetypes.user_duty_transactions.requests_display')}
      </SettingsForm.Section.Header>
      <SettingsForm.Section.Content>
        <SettingsForm.RadioButtonGroup fieldName="viewType" labelColWidth={7} options={viewTypeOptions()} />
      </SettingsForm.Section.Content>
    </SettingsForm.Section>
    <SettingsForm.Section>
      <SettingsForm.Section.Header>
        {I18n.t('dashboards.widget_archetypes.user_duty_transactions.requests_to_show')}
      </SettingsForm.Section.Header>
      <SettingsForm.Section.Content>
        {allTypeIds.map(type => (
          <SettingsForm.ToggleField
            key={type}
            fieldName={`show_${type}`}
            label={<DutyTransactionTypeIndicator typeId={type} />}
          />
        ))}
      </SettingsForm.Section.Content>
    </SettingsForm.Section>
  </SettingsForm>
);

const UserDutyTransactionsWidget = ({
  acceptTransaction,
  dutyTransactions,
  fetchCurrentUser,
  currentUserId,
  rejectTransaction,
  userId,
  viewType,
  dashboardAPIManager,
  dashboardDateTime,
  widgetId,
  show_duty_exchange_outgoing,
  show_duty_exchange_incoming,
  show_absence_request,
  show_duty_request,
  show_extra_work_request
}) => {
  const [_lastUpdatedAt, setLastUpdatedAt] = useState(Date.now());
  const [acceptingTransaction, setAcceptingTransaction] = useState(null);
  const widgetUserId = userId || currentUserId;
  // ignore dashboard date time if we're in standalone (non-widget) mode
  if (!widgetId) dashboardDateTime = null;

  const requestsDisplay = viewType || 'my_requests';
  const requestsToShowSelection = widgetId
    ? [
        show_duty_exchange_outgoing && 'duty_exchange_outgoing',
        show_duty_exchange_incoming && 'duty_exchange_incoming',
        show_absence_request && 'absence_request',
        show_duty_request && 'duty_request',
        show_extra_work_request && 'extra_work_request'
      ]
    : allTypeIds;

  // by default, show all states, all types, ending after beginning of today
  const [filtered, setFiltered] = useState([
    { id: 'type', value: requestsToShowSelection },
    { id: 'state', value: allStates },
    { id: 'userState', value: [...allUserStates, null] },
    { id: 'type', value: requestsToShowSelection || allTypeIds },
    { id: 'start_time', value: dashboardDateTime || moment().startOf('day') }
  ]);

  useEffect(() => {
    if (widgetId && dashboardDateTime)
      setFiltered([...filtered.filter(i => i.id != 'start_time'), { id: 'start_time', value: dashboardDateTime }]);
  }, [dashboardDateTime]);

  dashboardAPIManager = dashboardAPIManager || new DashboardAPIManager();

  useEffect(() => {
    if (!widgetUserId) return;
    fetchData();
    const updateInterval = FIFTEEN_MINUTES;
    const interval = setInterval(fetchData, updateInterval);
    return () => clearInterval(interval);
  }, [widgetUserId, dashboardDateTime]);

  const fetchData = () => {
    fetchCurrentUser();
    fetchDutyTransactionsForPeriod();
    dashboardAPIManager.fetchUsers({ params: { presentation_type: 'basic' } });
    dashboardAPIManager.fetchAvailabilityCodes();
    dashboardAPIManager.fetchGroups({ params: { regarding_user_id: widgetUserId } });
    setLastUpdatedAt(Date.now());
  };

  const fetchDutyTransactionsForPeriod = (startTime, endTime) => {
    startTime = startTime || dashboardDateTime || moment().startOf('day');
    dashboardAPIManager.fetchDutyTransactions({
      startTime,
      endTime,
      forceFetch: true,
      params: { regarding_user_id: requestsDisplay === 'my_requests' ? widgetUserId : undefined }
    });
  };

  // If we are in widget mode, we don't show the date and type filters in the table
  // this is to save space in the dashboard
  const showDatePicker = !widgetId;
  const showRequestTypeFilter = !widgetId;

  const acceptTransactionLocal = useCallback(
    async transaction => {
      setAcceptingTransaction(transaction);
      await acceptTransaction(transaction);
      setAcceptingTransaction(null);
    },
    [acceptTransaction]
  );

  const retryAcceptTransaction = useCallback(async () => {
    await acceptTransaction(acceptingTransaction, true);
    setAcceptingTransaction(null);
  }, [acceptingTransaction, acceptingTransaction]);

  const columns = useMemo(
    () =>
      instantiateColumns({
        widgetUserId,
        acceptTransaction: acceptTransactionLocal,
        rejectTransaction,
        showDatePicker,
        showRequestTypeFilter,
        acceptingTransaction
      }),
    [widgetUserId, acceptTransactionLocal, rejectTransaction, acceptingTransaction]
  );

  // select only transactions that relate to the user:
  // - requested absence requests
  // - requested and offered duty exchanges
  // - offered duty requests
  const transactions = useMemo(
    () =>
      dutyTransactions
        .filter(t => t.state !== 'withdrawn')
        .filter(t => (requestsDisplay === 'my_requests' ? isRegardingUser(t, widgetUserId) : true)),
    [dutyTransactions, widgetUserId, requestsDisplay]
  );

  return (
    <>
      <ScheduleWarningsPopoverConnected
        handleUpdated={retryAcceptTransaction}
        handleDismissed={() => setAcceptingTransaction(null)}
      />
      <ReactTable
        // by default, sort by start time
        className="duty-transaction-table"
        defaultSorted={[{ id: 'start_time' }]}
        onFilteredChange={(filtered, column) => {
          setFiltered(filtered);

          if (!['start_time', 'end_time'].includes(column.id)) return;

          const startTimeFilter = filtered.find(f => f.id === 'start_time');
          const endTimeFilter = filtered.find(f => f.id === 'end_time');
          const startTime = (startTimeFilter || {}).value;
          const endTime = (endTimeFilter || {}).value;

          fetchDutyTransactionsForPeriod(startTime, endTime);
        }}
        filtered={filtered}
        filterable
        data={transactions}
        columns={columns}
      />
    </>
  );
};

Object.assign(UserDutyTransactionsWidget, {
  SettingsContent: Settings,
  defaultSettings: {
    viewType: 'my_requests',
    show_duty_exchange_outgoing: true,
    show_duty_exchange_incoming: true,
    show_absence_request: true,
    show_duty_request: true,
    show_extra_work_request: true
  }
});

const mapStateToProps = state => ({
  dutyTransactions: dutyTransactionSelectors.values(state),
  dutyTransactionsInitialLoadComplete: dutyTransactionSelectors.selectInitialLoadComplete(state),
  dashboardDateTime: state.dashboards.dashboardDatetime.dateTime,
  currentUserId: selectCurrentUserId(state),
  currentDashboardId: state.dashboards.currentDashboardId
});

const mapDispatchToProps = {
  acceptTransaction: dutyTransactionOperations.acceptTransaction,
  rejectTransaction: dutyTransactionOperations.rejectTransaction,
  fetchCurrentUser: getCurrentUser
};

export default connect(mapStateToProps, mapDispatchToProps)(UserDutyTransactionsWidget);
